import { useEffect, useState } from 'react'
import type { Partner } from '@shared/types'
import type { Statement } from '@shared/commissions'
import { formatGBP } from '@shared/money'
import { KpiTile } from '../components/KpiTile.js'
import {
  claimCommissions, fetchCommissions, markCommissionsPaid, reopenCommissions,
  type CommissionPartnerBlock,
} from '../lib/api.js'

/**
 * Claimable fees (decision 183) — one component, two audiences.
 *
 *  - An AGENT partner session sees its own statements and the Claim button.
 *    It never sees another agent, and it cannot mark itself paid.
 *  - A Wave9 ADMIN sees every agent, and owns "Mark paid" and "Reopen".
 *
 * Splitting these into two pages would have meant two renderings of the same
 * statement drifting apart — the difference is genuinely only scope and which
 * two buttons appear, so it is one component with an `admin` prop. The API
 * enforces both boundaries independently; the props only decide what to draw.
 *
 * The flow (decision 14, unchanged since July): rows accrue at acceptance →
 * the agent claims, which stamps one statement and says an invoice is coming →
 * finance marks it paid, which writes the fee-paid date onto each HubSpot deal.
 */

export function Commissions({ partner, admin, onOpen }: {
  partner: Partner | null
  admin: boolean
  onOpen: (quoteId: string) => void
}) {
  const [data, setData] = useState<{ agent: boolean; partners: CommissionPartnerBlock[]; truncated?: boolean; cap?: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [invoiceRef, setInvoiceRef] = useState('')

  const load = () => {
    fetchCommissions()
      .then((r) => { setData(r); setError(null) })
      .catch((e: Error) => setError(e.message))
  }
  useEffect(load, [])

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn(); load() }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }

  const claim = (partnerId: string, count: number, total: number) => {
    const ref = invoiceRef.trim()
    if (!confirm(
      `Claim ${count} accepted ${count === 1 ? 'quote' : 'quotes'} — ${formatGBP(total)}?\n\n`
      + 'This tells Wave9 an invoice is on its way and closes this statement. '
      + 'Fees accrued after now start a fresh one.',
    )) return
    void run(() => claimCommissions({ agentInvoiceRef: ref || undefined, partnerId: admin ? partnerId : undefined }))
      .then(() => setInvoiceRef(''))
  }

  const pay = (s: Statement) => {
    if (!s.claimId) return
    if (!confirm(
      `Mark ${formatGBP(s.total)} paid?\n\n`
      + 'The fee-paid date is written onto each quote\'s HubSpot deal on the next sync.',
    )) return
    void run(() => markCommissionsPaid(s.claimId!))
  }

  const reopen = (s: Statement) => {
    if (!s.claimId) return
    const reason = prompt(
      s.status === 'paid'
        ? 'Reopening a PAID statement — it goes back to awaiting payment and the fee-paid date is cleared in HubSpot.\n\nWhy?'
        : 'Reopening this statement — the fees return to claimable and the invoice reference is dropped.\n\nWhy?',
    )
    if (reason === null) return
    if (!reason.trim()) { setError('Say why this statement is being reopened — it is a financial record.'); return }
    void run(() => reopenCommissions(s.claimId!, reason.trim()))
  }

  if (error && !data) return <p className="pill pill--stop">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  // Decisions 19/27: only agents earn a fee, so a reseller or internal session
  // gets told that plainly rather than shown an empty statement.
  if (!data.agent) {
    return (
      <>
        <div className="page-head"><div><h1>Claimable fees</h1></div></div>
        <div className="card"><div className="card__body">
          <p className="muted">
            Referral fees are an agent arrangement. This partner quotes as a{' '}
            {partner?.mode === 'reseller' ? 'reseller — margin lives on the quote itself' : 'Wave9 team'}, so
            there is nothing to claim here.
          </p>
        </div></div>
      </>
    )
  }

  const blocks = data.partners
  const nothing = blocks.every((b) => b.statements.length === 0)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Claimable fees</h1>
          <p>
            {admin
              ? 'Every agent\'s referral fees. A fee accrues when a customer accepts; the agent claims a statement and invoices Wave9; you mark it paid, which writes the fee-paid date onto the HubSpot deal.'
              : 'Your referral fees. One accrues each time a customer accepts a quote. Claim a statement, then invoice Wave9 for it — Wave9 marks it paid when the invoice is settled.'}
          </p>
        </div>
      </div>

      {error && <p className="pill pill--stop">{error}</p>}
      {data.truncated && (
        <p className="pill pill--warn" style={{ display: 'block', marginBottom: 12 }}
          title="Claiming or marking paid still moves EVERY row in the state — only this view is capped.">
          Showing the oldest {data.cap} ledger rows — the totals here are lower than the ledger holds. Ask for the cap to be raised before relying on these figures.
        </p>
      )}

      {nothing && (
        <div className="card"><div className="card__body">
          <p className="muted small">
            Nothing accrued yet. A fee appears here the moment a customer accepts an agent quote —
            the amount frozen at that moment, never recalculated afterwards.
          </p>
        </div></div>
      )}

      {blocks.map((block) => (
        <div key={block.partnerId} className="card">
          <div className="card__head">
            <h2 style={{ margin: 0 }}>{admin ? block.partnerName : 'Your statements'}</h2>
            <span className="muted small">
              {block.totals.claimableCount} claimable · {block.totals.awaitingPaymentCount} awaiting payment
            </span>
          </div>
          <div className="card__body">
            <div className="kpis" style={{ marginBottom: 12 }}>
              <KpiTile label="Claimable now" value={formatGBP(block.totals.claimable)}
                sub={`${block.totals.claimableCount} accepted ${block.totals.claimableCount === 1 ? 'quote' : 'quotes'}`} />
              <KpiTile label="Awaiting payment" value={formatGBP(block.totals.awaitingPayment)}
                sub={block.totals.awaitingPaymentCount ? 'invoiced to Wave9' : 'nothing outstanding'} />
              <KpiTile label="Paid to date" value={formatGBP(block.totals.paid)}
                sub={`${block.totals.paidCount} settled`} />
            </div>

            {block.statements.map((s) => (
              <div key={s.claimId ?? 'claimable'} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <strong>{formatGBP(s.total)}</strong>{' '}
                    <span className="muted">
                      · {s.count} {s.count === 1 ? 'quote' : 'quotes'}
                    </span>{' '}
                    {s.claimId === null && <span className="pill">claimable</span>}
                    {s.status === 'invoice_received' && s.claimId && <span className="pill pill--info">awaiting payment</span>}
                    {s.status === 'paid' && <span className="pill pill--ok">paid</span>}
                    <div className="small muted">
                      {s.claimId === null && 'Everything accepted and not yet claimed.'}
                      {s.invoicedAt && <>Claimed {new Date(s.invoicedAt).toLocaleDateString('en-GB')}</>}
                      {s.agentInvoiceRef && <> · invoice {s.agentInvoiceRef}</>}
                      {s.paidAt && <> · paid {new Date(s.paidAt).toLocaleDateString('en-GB')}</>}
                    </div>
                  </div>

                  {s.claimId === null && (
                    <div className="toolbar" style={{ gap: 6 }}>
                      <input className="input" style={{ width: 170 }} placeholder="Your invoice ref (optional)"
                        value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} maxLength={64} />
                      <button className="btn btn--primary btn--sm" disabled={busy}
                        onClick={() => claim(block.partnerId, s.count, s.total)}>
                        {admin ? 'Claim on their behalf' : 'Claim these fees'}
                      </button>
                    </div>
                  )}
                  {admin && s.status === 'invoice_received' && s.claimId && (
                    <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => pay(s)}>Mark paid</button>
                  )}
                  {admin && s.claimId && (
                    <button className="btn btn--sm" disabled={busy} onClick={() => reopen(s)}
                      title="Correct a mistake — logs who reopened it and why.">Reopen</button>
                  )}
                </div>

                <table className="table" style={{ marginTop: 8 }}>
                  <thead><tr><th>Quote</th><th>Customer</th><th>Accepted</th><th style={{ textAlign: 'right' }}>Fee</th><th /></tr></thead>
                  <tbody>
                    {s.rows.map((r) => (
                      <tr key={r.id}>
                        <td><strong>{r.quoteNumber}</strong></td>
                        <td>{r.customerName ?? '—'}</td>
                        <td>{r.acceptedAt ? new Date(r.acceptedAt).toLocaleDateString('en-GB') : '—'}</td>
                        <td style={{ textAlign: 'right' }}>{formatGBP(r.amount)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn--sm" disabled={!partner} onClick={() => onOpen(r.quoteId)}>Open →</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
